import React, { ReactNode } from 'react';
import api from 'controllers/raymanAPI';
import {FilesCtxProvider, FilesCtx} from './files';
import Singletons from 'controllers/Singletons';

interface TaskEdit
{
    procedureID: number,
    minStartDate: Date | null,
    maxStartDate: Date | null,
    name: string,
    priority: "low" | "normal" | "important" | "urgent",
    info: string,
    asignees: number[],
    tags: number[]
}

interface APITask extends TaskEdit
{
    taskID: number,
    schedulerID: number | null,
    procedureDescription: string | null,
    procedureName: string | null,
    creatorUser: number,
    lastModificationUser: number,
    lastModificationDatetime: Date,
    status: "completed" | "in_progress" | "not_started"
}
interface Task extends APITask
{
    id: number,
    assignedToUser: boolean
}
type TasksList = {
    array: Task[],
    indexed: 
    {
        [key: number]: Task
    }  
}

type TasksCtxType = {
    loading: boolean,
    data: TasksList
    funcs: {
        create: (task: TaskEdit) => Promise<APITask | null>,
        update: (id: number, task: TaskEdit) => Promise<void>,
        delete: (taskID: number) => Promise<void>
    }
}


function map(item : APITask) : Task
{
    return {...item, id: item.taskID, assignedToUser: (item.asignees.includes(Singletons.currentUser.userID))};
}

function copyProperties(from : APITask, to: APITask)
{
    if(from.procedureID) to.procedureID = from.procedureID;
    if(from.minStartDate) to.minStartDate = from.minStartDate;
    if(from.maxStartDate) to.maxStartDate = from.maxStartDate;
    if(from.name) to.name = from.name;
    if(from.priority) to.priority = from.priority;
    if(from.info) to.info = from.info;
    if(from.asignees) to.asignees = from.asignees;
    if(from.tags) to.tags = from.tags;
    if(from.taskID) to.taskID = from.taskID;
    if(from.schedulerID) to.schedulerID = from.schedulerID;
    if(from.procedureDescription) to.procedureDescription = from.procedureDescription;
    if(from.procedureName) to.procedureName = from.procedureName;
    if(from.creatorUser) to.creatorUser = from.creatorUser;
    if(from.lastModificationUser) to.lastModificationUser = from.lastModificationUser;
    if(from.lastModificationDatetime) to.lastModificationDatetime = from.lastModificationDatetime;
    if(from.status) to.status = from.status;
}

async function getData() : Promise<TasksList>
{
    let indexed : {[key: number]: Task} = {};
    let response = await api.procedures.tasks.getAll(Singletons.currentUser.profiles.includes("administrators") ? "administrators" :
                                                     Singletons.currentUser.profiles.includes("experts") ? "experts" : "operators");
    if(!response) throw new Error("Response is null");
    let data : Task[] = await response.json();
    data = data.map((item : APITask)=>map(item));
    data.forEach((item : Task) => {
        indexed[item.taskID] = item
    });
    
    return {array: data, indexed: indexed};
}

const TasksCtx = React.createContext<TasksCtxType>({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        create: async () => null,
        update: async () => {},
        delete: async () => {},
    }
});

function TasksCtxProvider(props : any)
{

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState<TasksList>({array: [], indexed: []});

    React.useEffect(() => {
        getData().then(values => {
            setData(values);
            setLoading(false);
        });
    }, []);

    const internalUpdate = React.useCallback((id : number, taskData : APITask) => {
        let tmp = {...data};
        copyProperties(taskData, tmp.indexed[id]);
        setData(tmp);
    }, [data]);

    const internalAdd = React.useCallback(async (id : number, item: APITask) => {
        if(data.indexed[id] !== undefined) console.log("duplicated id", id);
        if(data.indexed[id] !== undefined) return internalUpdate(id, map(item));
        console.log("added id", id);
        let tmp = {...data};
        let mapped = map(item);
        let index = tmp.array.push(mapped);
        tmp.indexed[mapped.id] = tmp.array[index-1];
        setData(tmp);
    }, [data, internalUpdate]);

    const internalDelete = React.useCallback((id: number) => {
        let tmp = {...data};
        let item = tmp.indexed[id];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);
        delete tmp.indexed[id];

        setData(tmp);

    }, [data]);


    const create = React.useCallback(async (data : TaskEdit) => {
        let result = await api.procedures.tasks.post(data);
        if(!result) throw new Error("Response is null");

        if(result.ok)
        {
            let item = await result.json();
            internalAdd(item.taskID, item);
            return item;
        }
        else return null;
    }, [internalAdd]);

    const update = React.useCallback(async (id: number, data: TaskEdit) => {
        let result = await api.procedures.tasks.updateSingle(id, data);
        if(!result) throw new Error("Response is null");
        if(result.ok) internalUpdate(id, map(await result.json()));

    }, [internalUpdate]);

    const remove = React.useCallback(async (id : number) => {
        let result = await api.procedures.tasks.deleteSingle(id);
        if(!result) throw new Error("Response is null");
        if(result.ok) internalDelete(id);
    }, [internalDelete]);

    React.useEffect( function handleNotifications() {

        const handleCreate = (message: APITask) => internalAdd(message.taskID, message);
        // const handleEnable = (message: APITask) => internalUpdate(message.taskID, {disabled: false});
        // const handleDisable = (message: APITask) => internalUpdate(message.taskID, {disabled: true});
        const handleUpdate = (message: APITask) => internalUpdate(message.taskID, map(message));
        const handleDelete = (message: APITask) => internalDelete(message.taskID);

        Singletons.NotificationsSocket?.on('procedures.tasks.created', handleCreate);
        // Singletons.NotificationsSocket?.on('procedures.tasks.disabled', handleDisable);
        // Singletons.NotificationsSocket?.on('procedures.tasks.enabled', handleEnable);
        Singletons.NotificationsSocket?.on('procedures.tasks.updated', handleUpdate);
        Singletons.NotificationsSocket?.on('procedures.tasks.deleted', handleDelete);

        return () => {
            Singletons.NotificationsSocket?.off('procedures.tasks.created', handleCreate);
            // Singletons.NotificationsSocket?.off('procedures.tasks.disabled', handleDisable);
            // Singletons.NotificationsSocket?.off('procedures.tasks.enabled', handleEnable);
            Singletons.NotificationsSocket?.off('procedures.tasks.updated', handleUpdate);
            Singletons.NotificationsSocket?.off('procedures.tasks.deleted', handleDelete);
        }

    }, [internalAdd, internalUpdate, internalDelete]);

    return <TasksCtx.Provider value={{loading: loading, data: {...data}, funcs: {create: create, update: update, delete: remove}}} >
        {props.children}
        </TasksCtx.Provider>
}


function TasksFilesProvider({taskID, children} : {taskID: number, children?: React.ReactNode})
{
    if(!taskID) return <>{children}</>;
    return <FilesCtxProvider apiEndpoint={api.procedures.tasks.files} apiParams={[taskID]}>
            <TasksFilesNotificationHandler taskID={taskID}>
                {children}
            </TasksFilesNotificationHandler>
        </FilesCtxProvider>
}

function TasksFilesNotificationHandler({taskID, children}: {taskID: number, children?: React.ReactNode})
{
    const filesCtx = React.useContext(FilesCtx);
    
    React.useEffect( function handleChatNotifications() {

        const addFile = (message: any) => {if(message.taskID === taskID) filesCtx.funcs.internal.add(message.fileInfo.fileID, message.fileInfo);}
        const deleteFile = (message: any) => {if(message.taskID === taskID) filesCtx.funcs.internal.delete(message.fileID);}
        const refreshFile = (message: any) => {if(message.taskID === taskID) filesCtx.funcs.internal.refresh(message.fileInfo.fileID);}

        Singletons.NotificationsSocket?.on('procedures.tasks.files.created', addFile);
        Singletons.NotificationsSocket?.on('procedures.tasks.files.deleted', deleteFile);
        Singletons.NotificationsSocket?.on('procedures.tasks.files.updated', refreshFile);

        return () => {
            Singletons.NotificationsSocket?.off('procedures.tasks.files.created', addFile);
            Singletons.NotificationsSocket?.off('procedures.tasks.files.deleted', deleteFile);
            Singletons.NotificationsSocket?.off('procedures.tasks.files.updated', refreshFile);
        }
    }, [filesCtx, taskID]);

    return <>{children}</>;

}

export type {TaskEdit, APITask, Task}
export {TasksCtx, TasksCtxProvider, TasksFilesProvider};
export {FilesCtx as TaskFilesCtx } from './files';